import {
  Button,
  Dropdown,
  DropdownBody,
  DropdownButton,
  DropdownItem,
} from "@components";
import { Floppy, XLg } from "react-bootstrap-icons";
import useViewsManagement from "../hooks/useViewsManagement";
import CreateEditViewModal from "./CreateEditViewModal";
import { PencilSquareIcon } from "@heroicons/react/24/outline";

const ViewsButton = ({ activeView, removeFilters, children }) => {
  return !activeView ? (
    children
  ) : (
    <div className="flex items-center h-8 gap-x-1 px-2.5 ring-1 ring-inset ring-zinc-300 rounded-md text-sm shadow-sm whitespace-nowrap">
      <Floppy />
      {children}
      <div
        onClick={removeFilters}
        className="ml-1 p-1 cursor-pointer hover:bg-zinc-200 shrink-0 rounded"
      >
        <XLg />
      </div>
    </div>
  );
};

const ViewsManagement = ({ configKey, filters, setFilters, scope }) => {
  const {
    viewsList,
    activeView,
    activeViewEdited,
    removeFilters,
    isSaveModalOpen,
    setSaveModalOpen,
    handleSaveView,
    handleUpdateView,
    resetFilters,
    applyView,
    editingView,
    editView,
    renameView,
    deleteView,
    trackEvent,
  } = useViewsManagement(configKey, filters, setFilters, scope);

  return (
    <>
      {viewsList.length > 0 && (
        <ViewsButton activeView={activeView} removeFilters={removeFilters}>
          <Dropdown>
            <DropdownButton>
              {!activeView ? (
                <Button color="secondary" className="h-8">
                  <Floppy className="" />
                  <span className="ms-2">Saved Filters</span>
                </Button>
              ) : (
                <button className="text-primary font-medium hover:bg-zinc-200 px-1 py-0.5 rounded">
                  {activeView.name}{" "}
                  <span className="text-zinc-500 font-normal">
                    {activeViewEdited ? "(edited)" : ""}
                  </span>
                </button>
              )}
            </DropdownButton>
            <DropdownBody>
              {viewsList.map((v, i) => (
                <DropdownItem
                  closeOnComplete
                  key={i}
                  onClick={() => applyView(v)}
                  className="group"
                >
                  <div className="flex items-center gap-x-1 justify-between w-full">
                    <span className="font-medium">{v.name}</span>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        editView(v);
                      }}
                      className="group-hover:block hidden -mr-2"
                    >
                      <PencilSquareIcon className="size-4 text-primary" />
                    </button>
                  </div>
                </DropdownItem>
              ))}
            </DropdownBody>
          </Dropdown>
        </ViewsButton>
      )}
      {!activeView && filters.length > 0 && (
        <Button
          color="primary"
          className="h-8"
          onClick={() => {
            trackEvent("save_filters");
            setSaveModalOpen(true);
          }}
        >
          <Floppy />
          <span className="ms-2">Save</span>
        </Button>
      )}
      {activeView && activeViewEdited && (
        <>
          <Button
            color="primary"
            className="h-8"
            onClick={() => {
              trackEvent("save_changes");
              handleUpdateView();
            }}
          >
            <Floppy />
            <span className="ms-2">Save</span>
          </Button>
          <Button
            color="secondary"
            className="h-8"
            onClick={() => {
              trackEvent("save_as");
              setSaveModalOpen(true);
            }}
          >
            Save as...
          </Button>
          <Button color="secondaryLink" className="h-8" onClick={resetFilters}>
            Reset
          </Button>
        </>
      )}
      <CreateEditViewModal
        isOpen={isSaveModalOpen || !!editingView}
        onClose={() => {
          setSaveModalOpen(false);
          editView(null);
        }}
        view={editingView}
        onSave={handleSaveView}
        onRename={renameView}
        onDelete={deleteView}
      />
    </>
  );
};

export default ViewsManagement;
